
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { deleteUser } from "@/services/userService";
import { TeamUser } from "@/components/settings/user-management/UserListItem";
import { logSecurityAction } from "@/services/securityLogService";

export const useUserDeletion = () => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUserForDeletion, setSelectedUserForDeletion] = useState<TeamUser | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  
  const { toast } = useToast();

  const openDeleteDialog = (user: TeamUser) => {
    setSelectedUserForDeletion(user);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteUser = async (adminPassword: string, notes: string) => {
    if (!selectedUserForDeletion) {
      toast({
        title: "Error",
        description: "No user selected for deletion",
        variant: "destructive",
      });
      return;
    }

    setIsDeletingUser(true);

    try {
      await deleteUser(selectedUserForDeletion.id, adminPassword, notes);
      
      // Log successful deletion in security logs
      await logSecurityAction(
        "user_deleted",
        "users",
        selectedUserForDeletion.id,
        { name: selectedUserForDeletion.name, email: selectedUserForDeletion.email, notes },
        "success"
      );
      
      toast({
        title: "User Deleted",
        description: `User ${selectedUserForDeletion.name} has been successfully deleted`,
      });
      
      // Return success to allow parent components to refresh data
      return true;
    } catch (error: any) {
      console.error("Error deleting user:", error);
      
      // Log failed deletion attempt
      if (selectedUserForDeletion) {
        try {
          await logSecurityAction(
            "user_deletion_failed",
            "users",
            selectedUserForDeletion.id,
            { error: error.message, notes },
            "failure"
          );
        } catch (logError) {
          console.error("Failed to log deletion error:", logError);
        }
      }
      
      toast({
        title: "Deletion Failed",
        description: error.message || "Failed to delete user",
        variant: "destructive",
      });
      
      throw error;
    } finally {
      setIsDeletingUser(false);
    }
  };

  return {
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    selectedUserForDeletion,
    isDeletingUser,
    openDeleteDialog,
    handleDeleteUser
  };
};
